import { useEffect, useState } from 'react'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { Button } from '@/shared/components/ui/button'
import { Card } from '@/shared/components/ui/card'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Switch } from '@/shared/components/ui/switch'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { AlertTriangle, CheckCircle2, Loader2, Plug, Settings, XCircle } from 'lucide-react'
import {
  useConfiguracion,
  useGuardarConfiguracion,
  useTestConnection,
} from '@/modules/bd-unificada/hooks/useConfig'
import type { TestConnectionResult } from '@/modules/bd-unificada/types/configuracion'

export function ConfiguracionPage() {
  useDocumentTitle('BD Unificada — Configuración')

  const { data: config, isLoading } = useConfiguracion()
  const guardar = useGuardarConfiguracion()
  const test = useTestConnection()

  const [apiBaseUrl, setApiBaseUrl] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [adminUser, setAdminUser] = useState('')
  const [adminPassword, setAdminPassword] = useState('')
  const [activo, setActivo] = useState(true)
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null)

  useEffect(() => {
    if (config) {
      setApiBaseUrl(config.api_base_url)
      setAdminUser(config.admin_user)
      setActivo(config.activo)
    }
  }, [config])

  const handleGuardar = () => {
    guardar.mutate({
      api_base_url: apiBaseUrl,
      api_key: apiKey || undefined,
      admin_user: adminUser,
      admin_password: adminPassword || undefined,
      activo,
    })
    setApiKey('')
    setAdminPassword('')
  }

  const handleTest = async () => {
    const result = await test.mutateAsync({
      api_base_url: apiBaseUrl || undefined,
      api_key: apiKey || undefined,
      admin_user: adminUser || undefined,
      admin_password: adminPassword || undefined,
    })
    setTestResult(result)
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-72 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
        <Settings className="h-6 w-6 text-blue-500" />
        BD Unificada — Configuración
      </h1>

      <Card className="border border-gray-300 dark:border-gray-700 p-6 space-y-5">
        <div className="space-y-1">
          <Label htmlFor="api_base_url">URL base del microservicio</Label>
          <Input
            id="api_base_url"
            placeholder="https://bd.tonalstrategies.com"
            value={apiBaseUrl}
            onChange={(e) => setApiBaseUrl(e.target.value)}
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="api_key">API Key (X-API-Key, lecturas)</Label>
          <Input
            id="api_key"
            type="password"
            placeholder={config?.tiene_api_key ? config.api_key_masked : 'Sin configurar'}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Déjalo en blanco para conservar la key guardada.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="admin_user">Usuario admin (JWT del microservicio)</Label>
            <Input
              id="admin_user"
              value={adminUser}
              onChange={(e) => setAdminUser(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="admin_password">Contraseña admin</Label>
            <Input
              id="admin_password"
              type="password"
              placeholder={config?.tiene_admin_password ? '••••••••••' : 'Sin configurar'}
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Déjalo en blanco para conservar la contraseña guardada.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Switch
            checked={activo}
            onCheckedChange={setActivo}
            className="data-[state=checked]:bg-blue-500"
          />
          <Label>Integración activa</Label>
        </div>

        {config?.ultimo_error && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm">
            <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-500" />
            <div>
              <p className="font-medium">Último error registrado</p>
              <p className="text-muted-foreground break-all">{config.ultimo_error}</p>
            </div>
          </div>
        )}

        {testResult && (
          <div
            className={`flex items-start gap-2 rounded-md border p-3 text-sm ${
              testResult.success
                ? 'border-green-300 bg-green-50 dark:bg-green-950/30'
                : 'border-red-300 bg-red-50 dark:bg-red-950/30'
            }`}
          >
            {testResult.success ? (
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-green-600" />
            ) : (
              <XCircle className="h-4 w-4 mt-0.5 text-destructive" />
            )}
            <div>
              <p className="font-medium">{testResult.mensaje}</p>
              {testResult.detalle && (
                <p className="text-muted-foreground break-all">{testResult.detalle}</p>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 pt-2">
          <Button
            variant="outline"
            onClick={handleTest}
            disabled={test.isPending}
          >
            {test.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Plug className="mr-2 h-4 w-4" />
            )}
            Probar conexión
          </Button>
          <Button
            onClick={handleGuardar}
            disabled={guardar.isPending}
            className="bg-blue-800 hover:bg-blue-900 text-white"
          >
            {guardar.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Guardar configuración
          </Button>
        </div>
      </Card>
    </div>
  )
}
